import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  CalendarDaysIcon,
  MegaphoneIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeSolid,
  CalendarDaysIcon as CalendarSolid,
  MegaphoneIcon as MegaphoneSolid,
  UserCircleIcon as UserSolid,
} from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';
import { useClubSettings } from '../../contexts/ClubSettingsContext';
import { boardAPI } from '../../services/api';
import { clubLogoSrc, handleClubLogoError } from '../../utils/clubLogo';
import { displayName } from '../../utils/brand';

interface Tab {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  activeIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Mely útvonalak tartoznak még ehhez a fülhöz. */
  match: (path: string) => boolean;
}

const TABS: Tab[] = [
  { name: 'Kezdőlap', href: '/', icon: HomeIcon, activeIcon: HomeSolid, match: (p) => p === '/' },
  {
    name: 'Program',
    href: '/program',
    icon: CalendarDaysIcon,
    activeIcon: CalendarSolid,
    match: (p) => p.startsWith('/program') || p.startsWith('/events'),
  },
  { name: 'Hírek', href: '/board', icon: MegaphoneIcon, activeIcon: MegaphoneSolid, match: (p) => p.startsWith('/board') },
  {
    name: 'Profil',
    href: '/me',
    icon: UserCircleIcon,
    activeIcon: UserSolid,
    match: (p) => p.startsWith('/me') || p.startsWith('/help'),
  },
];

/**
 * A szülők és a játékosok kerete: mobilon alul négy fül, nagyobb képernyőn
 * felül egy keskeny sáv ugyanazokkal a fülekkel, a tartalom középen, keskeny
 * hasábban - hogy telefonon és gépen is ugyanaz az app legyen.
 */
const FamilyLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { settings } = useClubSettings();
  const location = useLocation();
  const [boardUnread, setBoardUnread] = useState(0);

  // Az üzenőfal "board-read" eseménnyel jelzi, ha valamit olvasottnak jelölt
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = () =>
      boardAPI
        .getUnreadCount()
        .then((count) => !cancelled && setBoardUnread(count))
        .catch(() => undefined);
    load();
    window.addEventListener('board-read', load);
    return () => {
      cancelled = true;
      window.removeEventListener('board-read', load);
    };
  }, [user]);

  // Fülváltáskor a lap tetejére
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const clubName = displayName(settings?.clubName);

  const badge = (tab: Tab) =>
    tab.href === '/board' && boardUnread > 0 ? (
      <span className="absolute -right-2 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold text-white ring-2 ring-white">
        {boardUnread > 9 ? '9+' : boardUnread}
      </span>
    ) : null;

  return (
    <div className="family-app min-h-screen bg-gray-100">
      {/* Nagyobb képernyőn felső sáv a fülekkel */}
      <header className="sticky top-0 z-40 hidden border-b border-gray-200 bg-white/90 backdrop-blur md:block">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-6 px-6">
          <Link to="/" className="flex min-w-0 items-center gap-2">
            <img
              src={clubLogoSrc(settings?.crestUrl)}
              alt=""
              className="h-8 w-8 object-contain"
              onError={handleClubLogoError}
            />
            <span className="truncate font-semibold text-gray-900">{clubName}</span>
          </Link>
          <nav className="ml-auto flex gap-1" aria-label="Fő menü">
            {TABS.map((tab) => {
              const active = tab.match(location.pathname);
              return (
                <Link
                  key={tab.href}
                  to={tab.href}
                  aria-current={active ? 'page' : undefined}
                  className={`relative rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  <span className="relative">
                    {tab.name}
                    {badge(tab)}
                  </span>
                </Link>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-4 pb-28 pt-safe-4 md:max-w-2xl md:pb-12 md:pt-6">{children}</main>

      {/* Mobilon alsó fülsor */}
      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 pb-safe-1 backdrop-blur md:hidden"
        aria-label="Fő menü"
      >
        <div className="mx-auto flex max-w-xl">
          {TABS.map((tab) => {
            const active = tab.match(location.pathname);
            const Icon = active ? tab.activeIcon : tab.icon;
            return (
              <Link
                key={tab.href}
                to={tab.href}
                aria-current={active ? 'page' : undefined}
                className={`flex flex-1 flex-col items-center gap-0.5 pb-1 pt-2 text-[11px] font-medium ${
                  active ? 'text-primary-600' : 'text-gray-500'
                }`}
              >
                <span className="relative">
                  <Icon className="h-7 w-7" />
                  {badge(tab)}
                </span>
                {tab.name}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default FamilyLayout;
